import { useRef, useState, type FC } from "react";
import SideNav from "../nav/SideNav";
import { ComponentContainer, JsonContainer, JsonHelperContainer, JsonTextContainer, StyledClearIcon, StyledCopyIcon } from "../jsonformatter/JsonFormatter.style";
import "ace-builds/src-noconflict/mode-json";
import Divider from "@mui/material/Divider";
import AdsComponent from "../AdsComponent";
import ace from "ace-builds/src-noconflict/ace";
import { ComponentBaseContainer } from "./JwtDecoder.style";
import { Helmet } from "react-helmet-async";

const JwtDecoder: FC = () => {

    const [outputJson, setOutputJson] = useState<string>('');
    const [showToast, setShowToast] = useState(false);
    const editorRef = useRef<any>(null);
    const outputRef = useRef<any>(null);
    const Range = ace.require("ace/range").Range;
    const decodeJwt = (token: string) => {
        try {
            highlightJwtSections(token);
            if (!token) {
                setOutputJson('');
                return;
            }
            const parts = token.split(".");
            if (parts.length !== 3) {
                setOutputJson("Invalid JWT token");
                return;
            }
            const [header, payload] = parts;

            const decode = (str: string) => {
                const base64 = str.replace(/-/g, "+").replace(/_/g, "/");
                const json = atob(base64);
                return JSON.stringify(JSON.parse(json), null, 2);
            };

            const decodedHeader = decode(header);
            const decodedPayload = decode(payload);

            const coloredOutput = `HEADER:\n${decodedHeader}\n\nPAYLOAD:\n${decodedPayload}`;

            setOutputJson(coloredOutput);
        } catch (err) {
            setOutputJson("Invalid JWT token");
        }
    };

    const handleClear = () => {
        editorRef.current?.editor?.setValue("", -1);
    }
    const handleCopy = () => {
        const content = outputRef.current?.editor?.getValue();
        content && navigator.clipboard.writeText(content)
            .then(() => {
                setShowToast(true);
                setTimeout(() => setShowToast(false), 1000);
            })
    }

    const highlightJwtSections = (token: string) => {
        const editor = editorRef.current?.editor;
        if (!editor) return;

        const session = editor.getSession();

        // Clear existing markers
        const markers = session.getMarkers(false);
        for (const key in markers) {
            session.removeMarker(markers[key].id);
        }

        const parts = token.split(".");
        if (parts.length !== 3) return;

        let startCol = 0;

        // Header
        session.addMarker(
            new Range(0, startCol, 0, startCol + parts[0].length),
            "jwt-header-highlight",
            "text"
        );
        startCol += parts[0].length + 1;

        // Payload
        session.addMarker(
            new Range(0, startCol, 0, startCol + parts[1].length),
            "jwt-payload-highlight",
            "text"
        );
        startCol += parts[1].length + 1;

        // Signature
        session.addMarker(
            new Range(0, startCol, 0, startCol + parts[2].length),
            "jwt-signature-highlight",
            "text"
        );
    };

    return (
        <ComponentBaseContainer>
            <Helmet>
                <title>JWT Decoder Online | Decode JSON Web Tokens</title>
                <meta name="description" content="Paste your JWT to decode and inspect its header, payload, and signature. Free JWT debugger tool online." />
                <meta name="keywords" content="jwt decoder, decode jwt, jwt debugger, jwt payload viewer, online jwt tool" />
            </Helmet>

            <SideNav />
            <ComponentContainer>
                <JsonContainer>
                    <JsonHelperContainer onClick={handleClear}>
                        <StyledClearIcon />
                    </JsonHelperContainer>
                    <Divider />
                    <JsonTextContainer
                        placeholder="Paste your JWT here..."
                        ref={editorRef}
                        mode="json"
                        name="jwt-editor"
                        wrapEnabled
                        onChange={decodeJwt}
                        setOptions={{
                            useWorker: false,
                            showLineNumbers: true,
                            tabSize: 2,
                        }}
                    />
                </JsonContainer>
                <Divider orientation="vertical" />
                <JsonContainer>
                    <JsonHelperContainer onClick={handleCopy}>
                        <StyledCopyIcon />
                    </JsonHelperContainer>
                    <Divider />
                    <JsonTextContainer
                        placeholder="Decoded JWT will appear here..."
                        ref={outputRef}
                        mode="json"
                        name="jwt-output"
                        value={outputJson}
                        wrapEnabled
                        readOnly
                        setOptions={{
                            useWorker: false,
                            showLineNumbers: true,
                            tabSize: 2,
                        }}
                    />
                </JsonContainer>
            </ComponentContainer>
            {showToast && (
                <div style={{
                    position: "absolute",
                    top: "10px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "#323232",
                    color: "white",
                    padding: "8px 16px",
                    borderRadius: "6px",
                    opacity: 0.9,
                    fontSize: "14px",
                }}>
                    Copied to clipboard!
                </div>
            )}
            <AdsComponent />
        </ComponentBaseContainer>
    );
}

export default JwtDecoder;
